import { createZodFormResolver, FormTextareaField, lt, showSuccessAlert } from '@mews/framework';
import { CommanderFormProps, useDialogFormControls } from '@mews-commander/form';
import { localizeCurrencyValue } from '@mews-commander/utils';
import { OverlayedLoader } from '@mews-commander/ui';
import { Stack } from '@optimus-web/core';
import { WithAccountId } from '@mews-commander/types';
import { DefaultFormProvider, useDefaultFormContext } from '../useDefaultFormValues';
import { GatewayPaymentFormField } from '../GatewayPaymentForm/schema';
import { PaymentAmountSection } from '../../../common/PaymentAmountSection';
import { PaymentMethodSelectField } from '../PaymentMethodSelectField/PaymentMethodSelectField';
import { ProcessPaymentFormField } from '../ProcessPaymentFormSchema';
import { useGetPaymentMethodsSuspense, useInvalidatePreauthorizationTableRecordQuery } from '../../../../queries';
import {
    useGetChargeablePreauthorizationSuspense,
    useInvalidateChargeablePreauthorizationQuery,
    useUpdatePreauthorization,
} from '../queries';
import { PaymentCommonSection } from '../../../common/PaymentCommonSection';
import { createUpdatePreauthorizationFormSchema } from './schema';

const UpdatePreauthorizationDialogFormContent = ({
    accountId,
    preauthorizationId,
    ...formProps
}: { preauthorizationId: string } & WithAccountId & CommanderFormProps) => {
    const invalidatePreauthorizationTableRecordQuery = useInvalidatePreauthorizationTableRecordQuery();
    const invalidateChargeablePreauthorizationQuery = useInvalidateChargeablePreauthorizationQuery({
        accountId,
        preauthorizationId,
    });

    const { data: chargeablePreauth } = useGetChargeablePreauthorizationSuspense({ accountId, preauthorizationId });
    const {
        data: { paymentCards },
    } = useGetPaymentMethodsSuspense({ accountId });

    const updatePreauthorization = useUpdatePreauthorization({ preauthorizationId, accountId });

    const resolver = createZodFormResolver(createUpdatePreauthorizationFormSchema(chargeablePreauth?.amount));
    const { defaultFormValues } = useDefaultFormContext();

    const { formRef, onSuccess, onSubmitted, onSubmitting } = formProps;
    const { renderFormContent } = useDialogFormControls({
        formRef,
        onSubmit: async (formValues) => {
            try {
                onSubmitting?.();
                const { amount, currencyCode, paymentMethodId } = formValues;

                await updatePreauthorization(formValues);

                showSuccessAlert({
                    description: lt('CardPreauthorizationSuccessMessage', {
                        cardLastDigits: paymentCards?.find((p) => p.id === paymentMethodId)?.lastDigits ?? '',
                        amount: localizeCurrencyValue({ value: amount, currency: currencyCode }),
                    }),
                });

                invalidatePreauthorizationTableRecordQuery({ accountId });
                invalidateChargeablePreauthorizationQuery();

                onSuccess?.();
            } finally {
                onSubmitted?.();
            }
        },
        resolver,
        defaultValues: {
            ...defaultFormValues,
            [GatewayPaymentFormField.PaymentMethodId]: chargeablePreauth?.paymentCardId ?? undefined,
            [ProcessPaymentFormField.Amount]: chargeablePreauth?.amount?.value,
            [ProcessPaymentFormField.CurrencyCode]: chargeablePreauth?.amount?.currencyCode,
            [ProcessPaymentFormField.ServiceOrderId]: chargeablePreauth?.serviceOrderId,
        },
    });

    return renderFormContent(
        <Stack vertical spacing="300">
            <PaymentCommonSection />
            <PaymentMethodSelectField
                required
                name={GatewayPaymentFormField.PaymentMethodId}
                label={lt('PaymentCard')}
                helper={lt('ProcessPaymentMewsPaymentsCardPaymentCardSelectionHint')}
                accountId={accountId}
                isExternal={!chargeablePreauth?.isGateway}
                readOnly
            />
            <PaymentAmountSection
                enabledCurrencies={[chargeablePreauth?.amount?.currencyCode ?? '']}
                isCurrencyFieldDisabled
            />
            <FormTextareaField name={ProcessPaymentFormField.Notes} label={lt('Notes')} rows={3} />
        </Stack>
    );
};

export const UpdatePreauthorizationDialogForm = ({
    accountId,
    preauthorizationId,
    ...formProps
}: { preauthorizationId: string } & WithAccountId & CommanderFormProps) => {
    const { isFetching } = useGetChargeablePreauthorizationSuspense({ accountId, preauthorizationId });

    if (isFetching) {
        return <OverlayedLoader show />;
    }

    return (
        <DefaultFormProvider accountId={accountId}>
            <UpdatePreauthorizationDialogFormContent
                accountId={accountId}
                preauthorizationId={preauthorizationId}
                {...formProps}
            />
        </DefaultFormProvider>
    );
};
